import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import UploadDialog from "@/components/UploadDialog";
import PetDialog from "@/components/PetDialog";
import PhotoCard from "@/components/PhotoCard";
import BirthdayStrip from "@/components/BirthdayStrip";
import { api, photoUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PawPrint, Plus, Heart, Camera, ArrowRight } from "lucide-react";
import { PET } from "@/constants/testIds";

export default function Dashboard() {
  const { user } = useAuth();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [petOpen, setPetOpen] = useState(false);
  const [pets, setPets] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [stats, setStats] = useState({ photos: 0, pets: 0, favorites: 0 });

  const load = async () => {
    const [petsRes, photosRes, statsRes] = await Promise.all([
      api.get("/pets"),
      api.get("/photos"),
      api.get("/stats"),
    ]);
    setPets(petsRes.data);
    setPhotos(photosRes.data);
    setStats(statsRes.data);
  };

  useEffect(() => { load(); }, []);

  const recent = photos.slice(0, 8);

  return (
    <div className="min-h-screen">
      <Nav onUpload={() => setUploadOpen(true)} />

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 relative z-10">
        {/* Hero */}
        <section className="mb-12">
          <div className="font-hand text-2xl text-[#E76F51] mb-1">hi {user?.name?.split(" ")[0] || "friend"},</div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold text-[#2D232E] tracking-tight leading-none">
            your little pack.
          </h1>
        </section>

        {/* Stats bento */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <StatCard label="Photos" value={stats.photos} icon={Camera} accent="#E76F51" />
          <StatCard label="Pets" value={stats.pets} icon={PawPrint} accent="#2A9D8F" />
          <StatCard label="Favorites" value={stats.favorites} icon={Heart} accent="#E9C46A" />
          <button
            onClick={() => setUploadOpen(true)}
            className="rounded-3xl border-2 border-dashed border-[#E76F51]/40 bg-[#E76F51]/5 p-5 flex items-center justify-center gap-2 text-[#E76F51] font-semibold hover:bg-[#E76F51]/10 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add memory
          </button>
        </section>

        <BirthdayStrip />

        {/* Pets */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-3xl font-bold text-[#2D232E]">My pets</h2>
            <button
              data-testid={PET.addBtn}
              onClick={() => setPetOpen(true)}
              className="inline-flex items-center gap-1.5 text-[#E76F51] font-semibold hover:underline"
            >
              <Plus className="w-4 h-4" /> New pet
            </button>
          </div>
          {pets.length === 0 ? (
            <div className="rounded-3xl bg-white border border-[#EAE2D6] p-10 text-center warm-shadow">
              <div className="w-16 h-16 rounded-full bg-[#F4EFE6] flex items-center justify-center mx-auto mb-4">
                <PawPrint className="w-7 h-7 text-[#E76F51]" />
              </div>
              <h3 className="font-display text-2xl font-bold text-[#2D232E]">No fluffs yet</h3>
              <p className="text-[#6A606C] mt-1 mb-5">Add your first pet to start building their scrapbook.</p>
              <button
                onClick={() => setPetOpen(true)}
                className="rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold px-6 py-2.5 shadow-[0_6px_20px_rgba(231,111,81,0.25)] transition-transform hover:scale-105 inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> Add your first pet
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
              {pets.map((pet) => (
                <Link
                  key={pet.id}
                  to={`/pets/${pet.id}`}
                  data-testid={PET.card}
                  className="group rounded-3xl bg-white border border-[#EAE2D6] p-4 warm-shadow hover:-translate-y-1 transition-transform"
                >
                  <div
                    className="w-full aspect-square rounded-2xl overflow-hidden mb-3 relative"
                    style={{ backgroundColor: `${pet.color}22` }}
                  >
                    {pet.cover_photo_id ? (
                      <img src={photoUrl(pet.cover_photo_id)} alt={pet.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <PawPrint className="w-10 h-10" style={{ color: pet.color }} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display font-semibold text-lg text-[#2D232E]">{pet.name}</div>
                      <div className="text-xs text-[#6A606C] capitalize">{pet.breed || pet.species}</div>
                    </div>
                    <span className="font-hand text-lg" style={{ color: pet.color }}>{pet.photo_count}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Recent */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-3xl font-bold text-[#2D232E]">Latest snaps</h2>
            <Link to="/gallery" className="inline-flex items-center gap-1 text-[#E76F51] font-semibold hover:underline">
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="rounded-3xl bg-white border border-[#EAE2D6] p-10 text-center warm-shadow">
              <div className="w-16 h-16 rounded-full bg-[#F4EFE6] flex items-center justify-center mx-auto mb-4">
                <Camera className="w-7 h-7 text-[#E76F51]" />
              </div>
              <h3 className="font-display text-2xl font-bold text-[#2D232E]">No memories yet</h3>
              <p className="text-[#6A606C] mt-1 mb-5">Upload your first photo — we&apos;ll even try to guess the breed.</p>
              <button
                onClick={() => setUploadOpen(true)}
                className="rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold px-6 py-2.5 shadow-[0_6px_20px_rgba(231,111,81,0.25)] inline-flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> Upload a photo
              </button>
            </div>
          ) : (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-6">
              {recent.map((p, i) => (
                <PhotoCard key={p.id} photo={p} index={i} pets={pets} onUpdate={load} onDelete={() => load()} onCoverChanged={load} />
              ))}
            </div>
          )}
        </section>
      </main>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={load} />
      <PetDialog open={petOpen} onOpenChange={setPetOpen} onSaved={load} />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-3xl bg-white border border-[#EAE2D6] p-5 warm-shadow relative overflow-hidden">
      <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full opacity-10" style={{ backgroundColor: accent }} />
      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${accent}22` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div className="font-display text-4xl font-bold text-[#2D232E]">{value}</div>
      <div className="text-sm text-[#6A606C]">{label}</div>
    </div>
  );
}
